import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  wizardEvalsApi,
  wizardEvalsKeys,
  type CreateRuleRequest,
  type CreateTestCaseRequest,
  type ImportRulePayload,
  type ManualOverrides,
  type UpdateRuleRequest,
  type UpdateTestCaseRequest,
} from '@/features/wizard-evals/api/wizard-evals-api';

export function useWizardEvalTestCases(params?: { carrier?: string }) {
  return useQuery({
    queryKey: wizardEvalsKeys.testCases(params),
    queryFn: () => wizardEvalsApi.listTestCases(params),
  });
}

export function useFetchIntakePayload() {
  return useMutation({
    mutationFn: (clientId: string) => wizardEvalsApi.fetchIntakePayload(clientId),
  });
}

export function useCreateWizardEvalTestCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTestCaseRequest) => wizardEvalsApi.createTestCase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.allTestCases() });
    },
  });
}

export function useUpdateWizardEvalTestCase(testCaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateTestCaseRequest) =>
      wizardEvalsApi.updateTestCase(testCaseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.allTestCases() });
    },
  });
}

export function useDeleteWizardEvalTestCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (testCaseId: string) => wizardEvalsApi.deleteTestCase(testCaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.allTestCases() });
    },
  });
}

export function useRunWizardEvalTestCase(testCaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => wizardEvalsApi.runSingleTestCase(testCaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: wizardEvalsKeys.runsByTestCase(testCaseId),
      });
      queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.runs() });
    },
  });
}

export function useWizardEvalRun(runId: string | null) {
  return useQuery({
    queryKey: wizardEvalsKeys.runDetail(runId ?? ''),
    queryFn: () => wizardEvalsApi.fetchRunByRunId(runId!),
    enabled: !!runId,
    gcTime: Infinity,
    refetchInterval: query => {
      const status = query.state.data?.run?.status;
      if (status === 'completed' || status === 'failed') return false;
      return 2000;
    },
  });
}

export function useWizardEvalRunsByTestCaseId(testCaseId: string) {
  return useQuery({
    queryKey: wizardEvalsKeys.runsByTestCase(testCaseId),
    queryFn: () => wizardEvalsApi.fetchRunsByTestCaseId(testCaseId),
    enabled: !!testCaseId,
  });
}

export function useActiveRunId(testCaseId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['wizardEvals', 'activeRun', testCaseId] as const;

  const { data } = useQuery<string | null>({
    queryKey,
    queryFn: () => null,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return {
    runId: data ?? null,
    setRunId: (runId: string) =>
      queryClient.setQueryData<string | null>(queryKey, runId),
  };
}

export function useWizardEvalRules(params?: { carrier?: string }) {
  return useQuery({
    queryKey: wizardEvalsKeys.rules(params),
    queryFn: () => wizardEvalsApi.listRules(params),
  });
}

export function useWizardEvalRunResults() {
  return useQuery({
    queryKey: wizardEvalsKeys.runResults(),
    queryFn: () => wizardEvalsApi.listRunResults(),
  });
}

export function useUpdateRunResultOverrides(runId: string | null, testCaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      runResultId,
      overrides,
    }: {
      runResultId: string;
      overrides: Pick<
        ManualOverrides,
        'fpReclassified' | 'fpIgnored' | 'fnAccepted' | 'fnIgnored' | 'note'
      >;
    }) => wizardEvalsApi.updateRunResultOverrides(runResultId, overrides),
    onSuccess: () => {
      if (runId) {
        queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.runDetail(runId) });
      }
      queryClient.invalidateQueries({
        queryKey: wizardEvalsKeys.runsByTestCase(testCaseId),
      });
    },
  });
}

export function useImportWizardEvalRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rules: ImportRulePayload[]) => wizardEvalsApi.importRules(rules),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.allRules() });
    },
  });
}

export function useCreateWizardEvalRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRuleRequest) => wizardEvalsApi.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.allRules() });
    },
  });
}

export function useUpdateWizardEvalRule(ruleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateRuleRequest) => wizardEvalsApi.updateRule(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.allRules() });
    },
  });
}

export function useDeleteWizardEvalRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => wizardEvalsApi.deleteRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardEvalsKeys.allRules() });
    },
  });
}
